import { API_URL } from '../config/config';
import { getRequest, patchRequest, postRequest, putRequest } from '../utils/axiosUtils';
import buildQueryParams from '../utils/buildQueryParams';
import { formatDuration } from '../utils/dateTime';
import { STATUS_LABEL } from '../utils/memberStatus';

export const fetchUserAnalytics = async (filter) => {
  const { country, state, city, dateRange, gender, utm_payload } = filter;
  const utmId = filter?.utmId || filter?.utm_id;

  const queryParams = buildQueryParams({
    country,
    state,
    city,
    gender,
    dateRange,
    utm_payload,
    utmId
  });

  const res = await getRequest({
    url: `${API_URL}/admin/analytics/stat/users?${queryParams}`,
  });
  if (!res) return
  if (res.status >= 200 && res.status < 300) {
    return res.data.data;
  } else {
    throw new Error(`Unexpected response code: ${res.status}`);
  }
};

export const fetchWeeklyUserAnalytics = async (filter) => {
  // const { country, state, city, dateRange, gender,utm_payload } = filter;

  // const queryParams = buildQueryParams({
  //   country,
  //   state,
  //   city,
  //   gender,
  //   dateRange,
  //   utm_payload,
  //   filter
  // });

  const res = await getRequest({
    url: `${API_URL}/admin/analytics/stat/getWeeklyUsers?${filter}`,
  });
  if (!res) return
  if (res.status >= 200 && res.status < 300) {
    return res.data.data;
  } else {
    throw new Error(`Unexpected response code: ${res.status}`);
  }
};
export const fetchMonthlyUserAnalytics = async (filter) => {
  // const { country, state, city, dateRange, gender,utm_payload } = filter;

  // const queryParams = buildQueryParams({
  //   country,
  //   state,
  //   city,
  //   gender,
  //   dateRange,
  //   utm_payload,
  //   filter
  // });

  const res = await getRequest({
    url: `${API_URL}/admin/analytics/stat/getMonthlyUsers?${filter}`,
  });
  if (!res) return
  if (res.status >= 200 && res.status < 300) {
    return res.data.data;
  } else {
    throw new Error(`Unexpected response code: ${res.status}`);
  }
};
export const fetchRealTimeUsersData = async (
  filter,
  setOnlineUsersCount,
  setData
) => {
  const { country, state, city, dateRange, gender, utm_payload, isBot } = filter;
  const utmId = filter?.utmId || filter?.utm_id;

  const queryParams = buildQueryParams({
    country,
    state,
    city,
    gender,
    dateRange,
    utm_payload,
    isBot,
    utmId
  });

  const res = await getRequest({
    url: `${API_URL}/admin/analytics/stat/realtime?${queryParams}`,
  });
  if (!res) return
  if (res.status >= 200 && res.status < 300 && res.data) {
    setOnlineUsersCount(res.data.data.onlineUsersCount);

    const IST_OFFSET_MINUTES = 5.5 * 60;
    const pastUsersData = res.data.data?.pastOnlineUsersCount.map((data) => {
      const utcDate = new Date(data.hourAgo);
      const istTime = new Date(utcDate.getTime() + IST_OFFSET_MINUTES * 60000);
      return [istTime.getTime(), data.activeUsersCount];
    });

    const sortedData = pastUsersData.sort((a, b) => a[0] - b[0]);

    setData(sortedData);
  } else {
    throw new Error(`Unexpected response code: ${res.status}`);
  }
};

export const fetchSubscriptionUsers = async (
  {
    filter,
    setUserData,
    page,
    limit,
    skip,
    verification = false,
    isBot = false,
    searchTerm
  }
) => {
  const { country, state, city, dateRange, gender, utm_payload, statsType } = filter;
  const utmId = filter?.utmId || filter?.utm_id;

  const queryParams = buildQueryParams({
    country,
    state,
    city,
    gender,
    dateRange,
    page,
    limit,
    skip,
    searchTerm,
    utm_payload,
    isBot,
    statsType,
    utmId
  });

  const res = await getRequest({
    url: `${API_URL}/admin/analytics/stat/subscription/users?${queryParams}`,
  });
  if (!res) return
  if (res.status >= 200 && res.status < 300 && res.data) {
    setUserData(res?.data?.data?.subscriptions);
    return res?.data?.data
  } else {
    throw new Error(`Unexpected response code: ${res.status}`);
  }
};

export const fetchUserStatData = async (userId, dateRange, setUserData) => {
  const queryParams = buildQueryParams({
    dateRange,
  });

  const res = await getRequest({
    url: `${API_URL}/admin/analytics/stat/user/${userId}?${queryParams}`,
  });
  if (!res) return
  if (res.status >= 200 && res.status < 300 && res.data) {
    setUserData(res.data.data);
  } else {
    throw new Error(`Unexpected response code: ${res.status}`);
  }
};

// { male: {count}, female: {count}, other: {count} } → card counts; a missing key reads 0.
const statusCardCounts = (stat) => ({
  maleCount: stat?.male?.count || 0,
  femaleCount: stat?.female?.count || 0,
  otherCount: stat?.other?.count || 0,
});

export const fetchUsersData = async (filter, userCardData, setData) => {
  const { country, state, city, dateRange, gender, utm_payload, isBot, profileCompilation, statsType, isIosUser } = filter;
  const utmId = filter?.utmId || filter?.utm_id;

  const queryParams = buildQueryParams({
    country,
    state,
    city,
    gender,
    dateRange,
    utm_payload,
    isBot,
    profileCompilation,
    statsType,
    utmId,
    ...(isIosUser === true || isIosUser === false ? { isIosUser } : {})
  });

  const res = await getRequest({
    url: `${API_URL}/admin/analytics/users?${queryParams}`,
  });
  if (!res) return
  if (res.status >= 200 && res.status < 300 && res.data) {
    const updatedData = userCardData.map((item) => {
      switch (item.title) {
        case 'Registered Users':
          return {
            ...item,
            maleCount: res.data.data.maleCount,
            femaleCount: res.data.data.femaleCount,
            otherCount: res.data.data.otherCount || 0,
          };
        // Status cards — titles come from memberStatus.js (see userCardData.js). activeUsers is
        // the Approved + Hidden total; approvedUsers / ghostUsers are absent on an older server → 0.
        case STATUS_LABEL.active:
          return { ...item, ...statusCardCounts(res.data.data.activeUsers) };
        case STATUS_LABEL.approved:
          return { ...item, ...statusCardCounts(res.data.data.approvedUsers) };
        case STATUS_LABEL.hidden:
          return { ...item, ...statusCardCounts(res.data.data.hiddenUserByAdmin) };
        case STATUS_LABEL.inactive:
          return { ...item, ...statusCardCounts(res.data.data.inActiveUser) };
        case STATUS_LABEL.ghost:
          return { ...item, ...statusCardCounts(res.data.data.ghostUsers) };
        case 'De-activated users':
          return {
            ...item,
            maleCount: res.data.data.deactivatedUsers.male.count,
            femaleCount: res.data.data.deactivatedUsers.female.count,
            otherCount: res.data.data.deactivatedUsers.other?.count || 0,
          };
        case 'Suspended User':
          return {
            ...item,
            maleCount: res.data.data.suspendedUsers
              ? res.data.data.suspendedUsers.male.count
              : 0,
            femaleCount: res.data.data.suspendedUsers
              ? res.data.data.suspendedUsers.female.count
              : 0,
            otherCount: res.data.data.suspendedUsers?.other?.count || 0,
          };
        case 'Permanently deleted':
          return {
            ...item,
            maleCount: res.data.data.permanentlyDeletedUsers.male.count,
            femaleCount: res.data.data.permanentlyDeletedUsers.female.count,
            otherCount: res.data.data.permanentlyDeletedUsers.other?.count || 0,
            value: res.data.data.permanentlyDeletedUsers.count,
          };
        case 'Verified':
          return {
            ...item,
            maleCount: res.data.data.verifiedUsers.male.count,
            femaleCount: res.data.data.verifiedUsers.female.count,
            otherCount: res.data.data.verifiedUsers.other?.count || 0,
          };
        case 'Subscribed':
          return {
            ...item,
            maleCount: res.data.data.subscribedUsers.male.count,
            femaleCount: res.data.data.subscribedUsers.female.count,
            otherCount: res.data.data.subscribedUsers.other?.count || 0,
          };
        case 'Average Age':
          return {
            ...item,
            maleCount: res.data.data.avgAge?.male ?? 0,
            femaleCount: res.data.data.avgAge?.female ?? 0,
            otherCount: res.data.data.avgAge?.other ?? 0,
          };
        case 'Blocked Profile':
          return {
            ...item,
            maleCount: res.data.data.blockedUsers.male.count,
            femaleCount: res.data.data.blockedUsers.female.count,
            otherCount: res.data.data.blockedUsers.other?.count || 0,
          };
        case 'Flag/Report':
          return {
            ...item,
            maleCount: res.data.data.flaggedUsers
              ? res.data.data.flaggedUsers.male.count
              : 0,
            femaleCount: res.data.data.flaggedUsers
              ? res.data.data.flaggedUsers.female.count
              : 0,
            otherCount: res.data.data.flaggedUsers?.other?.count || 0,
          };
        case 'Device':
          return {
            ...item,
            // value: res.data.data.uniqueDevicesCount,
            value: res.data.data.deviceCounts.reduce((acc, curr) => acc + curr.count, 0),
            count: res.data.data.deviceCounts
          };
        case 'Country':
          return {
            ...item,
            value: res.data.data.uniqueCountriesCount,
          };
        default:
          return item;
      }
    });

    setData(updatedData);
  } else {
    throw new Error(`Unexpected response code: ${res.status}`);
  }
};

// The query string a member list (User Management, Bot Management, Campaign Members) sends
// to getUsers. Shared with fetchUsersCountryCounts so the country dropdown counts the same
// population the list shows.
const buildUsersListQuery = ({
  filter,
  page,
  limit,
  skip,
  verification = false,
  isSelfieVerification = false,
  isIdVerification = false,
  searchTerm,
}) => {
  const path = typeof window !== "undefined" ? window.location.pathname : "";

  let isBotFinal = filter?.isBot;
  if (path.includes("bot-management")) {
    isBotFinal = true;
  } else if (path.includes("user-management")) {
    isBotFinal = false;
  }

  const { country, state, city, dateRange, gender, activeVerificationTab, utm_payload, sortBy, sortOrder, profileCompilation, requestType, statsType, utmId, isIosUser, source, channel, campaign, adset, creative, platform, from, to, status } = filter;

  return buildQueryParams({
    country,
    state,
    city,
    gender,
    dateRange,
    verification,
    activeVerificationTab,
    page,
    limit,
    skip,
    isBot: isBotFinal,
    isSelfieVerification,
    isIdVerification,
    searchTerm,
    utm_payload,
    sortBy,
    sortOrder,
    profileCompilation,
    requestType,
    statsType,
    utmId,
    ...(source ? { source } : {}),
    // Channel-attribution node params — forwarded only when source === 'attribution'.
    ...(source === 'attribution' ? { channel, campaign, adset, creative, platform, from, to, status } : {}),
    ...(isIosUser === true || isIosUser === false ? { isIosUser } : {})
  });
};

export const fetchUsersList = async ({ setUserData, ...args }) => {
  const queryParams = buildUsersListQuery(args);

  const res = await getRequest({
    url: `${API_URL}/admin/analytics/getUsers?${queryParams}`,
  });
  if (!res) return
  if (res.status >= 200 && res.status < 300 && res.data) {
    setUserData(res.data.data.users);
    return res.data.data;
  } else {
    throw new Error(`Unexpected response code: ${res.status}`);
  }
};

/**
 * How many of the CURRENT member list's members are in each country — the country
 * dropdown's counts on User Management, Bot Management and Campaign Members. Same query
 * as fetchUsersList (every filter, the search, the attribution node); the server ignores
 * the list's own country/state/city so the dropdown still offers the other countries.
 * Its own path, so it never cancels the list's request (getRequest aborts by path).
 */
export const fetchUsersCountryCounts = async ({ filter }) => {
  const queryParams = buildUsersListQuery({
    filter,
    page: 1,
    limit: 1,
    skip: 0,
    searchTerm: filter?.searchTerm,
  });
  const res = await getRequest({
    url: `${API_URL}/admin/analytics/getUsersCountryCounts?${queryParams}`,
  });
  if (!res) return null;
  if (res.status >= 200 && res.status < 300 && res.data) {
    return (res.data.data && res.data.data.countries) || [];
  }
  throw new Error(`Unexpected response code: ${res.status}`);
};

export const suspendUser = async ({ userId, suspendReason = "" }) => {
  const res = await postRequest({
    url: `${API_URL}/admin/user/suspend/${userId}`,
    data: { suspendReason },
  });

  if (res.status >= 200 && res.status < 300) {
    return res.data;
  } else {
    throw new Error(`Unexpected response code: ${res.status}`);
  }
};

export const invisibleUser = async ({ userId, hideReason = "" }) => {
  const res = await postRequest({
    url: `${API_URL}/admin/user/invisible/${userId}`,
    data: { hideReason }
  });

  if (res.status >= 200 && res.status < 300) {
    return res.data;
  } else {
    throw new Error(`Unexpected response code: ${res.status}`);
  }
};
export const shadowHideUser = async ({ userId, hideReason = "" }) => {
  const res = await postRequest({
    url: `${API_URL}/admin/user/shadow-hide/${userId}`,
    data: { hideReason }
  });

  if (res.status >= 200 && res.status < 300) {
    return res.data;
  } else {
    throw new Error(`Unexpected response code: ${res.status}`);
  }
};
export const demoteUser = async ({ userId }) => {
  const res = await postRequest({
    url: `${API_URL}/admin/user/demote/${userId}`,
    data: {}
  });

  if (res.status >= 200 && res.status < 300) {
    return res.data;
  } else {
    throw new Error(`Unexpected response code: ${res.status}`);
  }
};

// Toggles a bot profile in/out of the fixed Delhi-NCR search block. The server
// derives the next state from the current one, so this takes no payload.
export const toggleNcrPin = async ({ userId }) => {
  const res = await postRequest({
    url: `${API_URL}/admin/user/ncr-pin/${userId}`,
    data: {}
  });

  if (res.status >= 200 && res.status < 300) {
    return res.data;
  } else {
    throw new Error(`Unexpected response code: ${res.status}`);
  }
};

export const fetchActivityLogsData = async (
  userId,
  dateRange,
  setActivityLogData,
  setTotalDuration
) => {
  const queryParams = buildQueryParams({
    dateRange,
  });

  const res = await getRequest({
    url: `${API_URL}/admin/analytics/stat/user/logs/${userId}?${queryParams}`,
  });
  if (!res) return
  if (res.status >= 200 && res.status < 300 && res.data) {
    let overallDuration = 0;

    const transformActivityLogData = (apiData) => {
      return apiData.map((item) => {
        const logs = item.logs.map((log) => ({
          time: log.time,
          device: log.device || [],
          profileView: log.profileView || 0,
          favorite: log.favorite || 0,
          session: log.session || 0,
          conversation: log.conversation || 0,
          sessionInfo: log.sessionInfo || null,
        }));

        const totalProfileView = logs.reduce(
          (sum, log) => sum + log.profileView,
          0
        );
        const totalFavorite = logs.reduce((sum, log) => sum + log.favorite, 0);
        const totalSession = logs.reduce((sum, log) => sum + log.session, 0);
        const totalConversation = logs.reduce(
          (sum, log) => sum + log.conversation,
          0
        );

        const dateDuration = logs.reduce((sum, log) => {
          const dur = log.sessionInfo?.durationMin || 0;
          overallDuration += dur; // Add to grand total
          return sum + dur;
        }, 0);

        // Format duration as "X min", "X hr Y min" etc.
        const durationStr = formatDuration(dateDuration);


        return {
          date: item.date,
          device: [...new Set(logs.flatMap((log) => log.device))],
          profileView: totalProfileView,
          duration: durationStr,
          favorite: totalFavorite,
          session: totalSession,
          conversation: totalConversation,
          logs: logs,
        };
      });
    };

    const transforrmedData = transformActivityLogData(res.data.data);

    setActivityLogData(transforrmedData);

    if (setTotalDuration) {
      setTotalDuration(res?.data?.summary?.duration ?? formatDuration(overallDuration));
    }

  } else {
    throw new Error(`Unexpected response code: ${res.status}`);
  }
};

export const fetchDailyActiveUsers = async (filter) => {
  const { country, state, city, dateRange, gender, utm_payload, isBot, utmId } = filter;

  const queryParams = buildQueryParams({
    country,
    state,
    city,
    gender,
    dateRange,
    utm_payload,
    isBot,
    utmId
  });

  const res = await getRequest({
    url: `${API_URL}/admin/analytics/stat/daily/active?${queryParams}`,
  });
  if (!res) return
  if (res.status >= 200 && res.status < 300) {
    return res.data.data;
  } else {
    throw new Error(`Unexpected response code: ${res.status}`);
  }
};

// Returns the user on success, or null when the request was aborted / carried no
// user. Callers that only need the setter can keep ignoring the return value —
// every existing call site does. `isAbort` is opt-out only: getRequest keys
// in-flight GETs on the path, so two components reading the SAME user id at once
// cancel each other. Pass { isAbort: false } from a caller that must not lose.
export const fetchUserData = async (userId, setUserData, isDeletedAccount, likedBy, options = {}) => {
  const { isAbort = true } = options;
  const likedByQuery = likedBy ? `?likedBy=${likedBy}` : "";
  const requestUrl = isDeletedAccount ? `${API_URL}/admin/user/get-deleted-account-by-id/${userId}` : `${API_URL}/admin/user/${userId}${likedByQuery}`
  const res = await getRequest({
    url: requestUrl,
    isAbort,
  });
  if (!res) return null
  if (res.status >= 200 && res.status < 300 && res.data) {
    setUserData(res.data.user);
    return res.data.user || null;
  } else {
    throw new Error(`Unexpected response code: ${res.status}`);
  }
};

export const fetchVerificationData = async (filter, setVerificationData) => {
  const { country, state, city, dateRange, gender, utm_payload, statsType } = filter;
  const utmId = filter?.utmId || filter?.utm_id;

  const queryParams = buildQueryParams({
    country,
    state,
    city,
    gender,
    dateRange,
    utm_payload,
    statsType,
    utmId
  });

  const res = await getRequest({
    url: `${API_URL}/admin/analytics/stat/verification?${queryParams}`,
  });
  if (!res) return
  if (res.status >= 200 && res.status < 300 && res.data) {
    setVerificationData(res.data.data?.result);
  } else {
    throw new Error(`Unexpected response code: ${res.status}`);
  }
};

export const verifyUser = async (
  userId,
  action,
  verificationType,
  rejectReason
) => {
  const queryParams = buildQueryParams({ action });

  let res;

  if (verificationType === 'Selfie Verification') {
    res = await putRequest({
      url: `${API_URL}/admin/user/verification/selfie/${userId}?${queryParams}`,
      data: { rejectReason },
    });
  } else if (verificationType === 'ID Verification') {
    res = await putRequest({
      url: `${API_URL}/admin/user/verification/document/${userId}?${queryParams}`,
      data: { rejectReason },
    });
  } else {
    throw new Error('Invalid verification type');
  }

  if (res.status >= 200 && res.status < 300 && res.data) {
    return res.data;
  } else {
    throw new Error(`Unexpected response code: ${res.status}`);
  }
};

export const verifyIosUser = async (userId, action) => {
  const queryParams = buildQueryParams({ action });

  const res = await putRequest({
    url: `${API_URL}/admin/user/verification/ios/${userId}?${queryParams}`,
    data: {},
  });

  if (res.status >= 200 && res.status < 300 && res.data) {
    return res.data;
  } else {
    throw new Error(`Unexpected response code: ${res.status}`);
  }
};

export const fetchBotUsers = async ({ data, setData, page, limit, skip, searchTerm, filter, reset = false }) => {
  const { country, city, state, utm_payload, filterType, dateRange } = filter;
  const utmId = filter?.utmId || filter?.utm_id;

  const queryParams = buildQueryParams({
    page,
    limit,
    skip,
    searchTerm,
    country,
    city,
    state,
    utm_payload,
    filterType,
    dateRange,
    utmId
  });

  const res = await getRequest({
    url: `${API_URL}/admin/user/botusers?${queryParams}`,
  });
  if (!res) return

  if (res.status >= 200 && res.status < 300 && res.data) {
    if (!reset)
      // setData((prevData) => [...prevData, ...res?.data?.data]);
      setData((prevData) => {
        const existingIds = new Set(prevData.map(item => item._id));
        const newData = res?.data?.data.filter(item => !existingIds.has(item._id));
        return [...prevData, ...newData];
      });
    else
      setData(res?.data?.data)
    return res.data;
  } else {
    throw new Error(`Unexpected response code: ${res.status}`);
  }
};

export const fetchBotChatUsers = async ({ userId, setData, sortBy, sortOrder = -1, unRead, searchTerm }) => {

  const queryParams = buildQueryParams({
    sortBy, sortOrder, unRead, searchTerm
  });

  const res = await getRequest({
    // url: `${API_URL}/admin/user/bot/${userId}?sortBy=lastMessage&sortOrder=-1`,
    url: `${API_URL}/admin/user/bot/${userId}?${queryParams}`,
  });
  if (!res) return
  if (res.status >= 200 && res.status < 300 && res.data) {
    setData(res?.data?.data);
    return res.data;
  } else {
    throw new Error(`Unexpected response code: ${res.status}`);
  }
};

export const fetchChatMessages = async (userId, roomId, setData) => {
  const res = await getRequest({
    url: `${API_URL}/admin/user/messages/${userId}?roomId=${roomId}`,
  });
  if (!res) return
  if (res.status >= 200 && res.status < 300 && res.data) {
    const msgList = Array.isArray(res?.data?.data) ? res?.data?.data : []
    const normalMessages = msgList?.filter(m => !m?.schedule);
    const scheduledMessages = msgList
      .filter(m => m?.schedule)
      .sort(
        (a, b) =>
          new Date(a?.scheduledDateTime) -
          new Date(b?.scheduledDateTime)
      );
    setData([...normalMessages, ...scheduledMessages]);

    return res.data;
  } else {
    throw new Error(`Unexpected response code: ${res.status}`);
  }
};

export const getBotUsers = async (userId, range, preferenceGender) => {
  const res = await getRequest({
    url: `${API_URL}/admin/user/getNearUsers/${userId}?range=${range}&preferenceGender=${preferenceGender}`,
  });
  if (!res) return
  if (res.status >= 200 && res.status < 300 && res.data) {
    return res.data;
  } else {
    throw new Error(`Unexpected response code: ${res.status}`);
  }
};
export const memberActionUpdateAPI = function ({
  memberId,
  data,
}) {
  const url = `${API_URL}/home/update-activity-log/${memberId}`;
  return putRequest({
    url,
    data,
  });
};

export const bostUserProfile = async (payload) => {
  const res = await patchRequest({
    url: `${API_URL}/admin/user/update-profile-level`,
    data: payload
  });

  if (res.status >= 200 && res.status < 300) {
    return res.data;
  } else {
    throw new Error(`Unexpected response code: ${res.status}`);
  }
};

// The member's signup story (Activity Logs → Signup journey): summary, timeline, recording start.
export const fetchSignupJourney = async (userId) => {
  const res = await getRequest({ url: `${API_URL}/admin/analytics/stat/user/journey/${userId}` });
  if (!res) return null;
  if (res.status >= 200 && res.status < 300 && res.data) return res.data.data;
  throw new Error(`Unexpected response code: ${res.status}`);
};
